import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { formatTimeMs } from '@/common/utils/audioPlayerUtils';
import type { GetCommentsQueryData } from '@/generated/graphql';
import { AuthorAvatar, authorName } from './AuthorAvatar';

export type CommentThread = GetCommentsQueryData['comments'][number];
type CommentItem = CommentThread | CommentThread['replies'][number];

export interface CommentThreadListProps {
  threads: CommentThread[];
  /** Current user id; edit is allowed only for the author. */
  userId?: string | null;
  canComment: boolean;
  canResolveComments: boolean;
  isOwner: boolean;
  highlightedThreadId?: string | null;
  /** Bump to re-scroll to the same highlighted thread. */
  highlightNonce?: number;
  /** Version being heard; timestamps of other versions are not seekable. */
  selectedVersion?: number;
  seekToMs: (ms: number) => void;
  onReply: (parentId: string, body: string) => Promise<unknown>;
  onEdit: (commentId: string, body: string) => Promise<unknown>;
  onDelete: (commentId: string) => Promise<unknown>;
  onResolve: (commentId: string) => Promise<unknown>;
  onReopen: (commentId: string) => Promise<unknown>;
  /** Project member display names by user id, used when the resolver never commented. */
  memberNames?: Record<string, string>;
}

export function CommentThreadList(props: CommentThreadListProps) {
  const {
    threads,
    userId,
    canComment,
    canResolveComments,
    isOwner,
    highlightedThreadId,
    highlightNonce,
    selectedVersion,
    seekToMs,
    onReply,
    onEdit,
    onDelete,
    onResolve,
    onReopen,
    memberNames,
  } = props;
  const [showResolved, setShowResolved] = useState(false);
  const [replyingId, setReplyingId] = useState<string | null>(null);
  const [replyBody, setReplyBody] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [menu, setMenu] = useState<{
    anchorEl: HTMLElement;
    item: CommentItem;
    canEdit: boolean;
    canDelete: boolean;
  } | null>(null);
  const rowRefs = useRef<Record<string, HTMLElement | null>>({});

  useEffect(() => {
    if (highlightedThreadId) {
      rowRefs.current[highlightedThreadId]?.scrollIntoView?.({
        block: 'nearest',
      });
    }
  }, [highlightedThreadId, highlightNonce]);

  /** Runs a mutation with a busy flag and inline error. Returns success. */
  async function run(
    busyKey: string,
    errKey: string,
    fn: () => Promise<unknown>
  ): Promise<boolean> {
    if (busy) return false;
    setBusy(busyKey);
    setErrors((e) => {
      const rest = { ...e };
      delete rest[errKey];
      return rest;
    });
    try {
      await fn();
      return true;
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      setErrors((prev) => ({ ...prev, [errKey]: message }));
      return false;
    } finally {
      setBusy(null);
    }
  }

  const errorAlert = (id: string) =>
    errors[id] ? (
      <Alert severity="error" sx={{ mt: 0.5 }} data-cy="comment-error">
        {errors[id]}
      </Alert>
    ) : null;

  const visible = threads.filter((t) => showResolved || !t.resolvedAt);

  async function saveEdit(item: CommentItem) {
    if (!editBody.trim() || busy) return;
    if (
      await run(`edit:${item._id}`, item._id, () =>
        onEdit(item._id, editBody.trim())
      )
    )
      setEditingId(null);
  }

  function renderMenuButton(item: CommentItem) {
    const isAuthor = !!userId && item.authorId === userId;
    if (!isAuthor && !isOwner) return null;
    return (
      <IconButton
        size="small"
        aria-label="More actions"
        data-cy="comment-more"
        aria-haspopup="menu"
        aria-expanded={menu?.item._id === item._id}
        aria-controls={
          menu?.item._id === item._id ? `comment-menu-${item._id}` : undefined
        }
        onClick={(e) =>
          setMenu({
            anchorEl: e.currentTarget,
            item,
            canEdit: isAuthor,
            canDelete: isAuthor || isOwner,
          })
        }
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
    );
  }

  function renderBody(item: CommentItem) {
    if (editingId === item._id) {
      return (
        <Box sx={{ mt: 0.5 }}>
          <TextField
            fullWidth
            multiline
            size="small"
            autoFocus
            value={editBody}
            onChange={(e) => setEditBody(e.target.value)}
            onKeyDown={(e) => {
              if (
                e.key === 'Enter' &&
                (e.metaKey || e.ctrlKey) &&
                !e.nativeEvent.isComposing
              ) {
                e.preventDefault();
                void saveEdit(item);
              }
            }}
            inputProps={{ maxLength: 2000, 'aria-label': 'edit comment' }}
          />
          <Box sx={{ mt: 0.5, display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="contained"
              data-cy="comment-edit-save"
              disabled={!editBody.trim() || !!busy}
              onClick={() => void saveEdit(item)}
            >
              Save
            </Button>
            <Button
              size="small"
              data-cy="comment-edit-cancel"
              onClick={() => setEditingId(null)}
            >
              Cancel
            </Button>
          </Box>
          {errorAlert(item._id)}
        </Box>
      );
    }
    return (
      <>
        {errorAlert(item._id)}
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
          {item.body}
          {item.editedAt && (
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
            >
              {' '}
              (edited)
            </Typography>
          )}
        </Typography>
      </>
    );
  }

  /** Resolver is matched against thread and reply authors; falls back to "Resolved". */
  function resolvedByLabel(thread: CommentThread) {
    if (!thread.resolvedBy) return 'Resolved';
    const commenter = [thread, ...thread.replies].find(
      (c) => c.authorId === thread.resolvedBy
    );
    const name = commenter
      ? authorName(commenter.author)
      : memberNames?.[thread.resolvedBy];
    return name ? `Resolved by ${name}` : 'Resolved';
  }

  function renderHeader(item: CommentItem, showTime: boolean) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AuthorAvatar author={item.author} decorative />
        {showTime && item.anchor && (
          <>
            {'versionNumber' in item && item.versionNumber != null && (
              <Chip
                size="small"
                variant="outlined"
                data-cy="comment-version-chip"
                label={`v${item.versionNumber}`}
              />
            )}
            {!('versionNumber' in item) ||
            item.versionNumber == null ||
            item.versionNumber === selectedVersion ? (
              <Chip
                size="small"
                clickable
                color="primary"
                variant="outlined"
                data-cy="comment-timestamp"
                label={formatTimeMs(item.anchor.atMs)}
                aria-label={`Seek to ${formatTimeMs(item.anchor.atMs)}`}
                sx={{
                  fontVariantNumeric: 'tabular-nums',
                  fontWeight: 600,
                  bgcolor: 'action.selected',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
                onClick={() => seekToMs(item.anchor.atMs)}
              />
            ) : (
              <Typography
                variant="caption"
                data-cy="comment-timestamp-text"
                sx={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {formatTimeMs(item.anchor.atMs)}
              </Typography>
            )}
          </>
        )}
        <Typography variant="caption" fontWeight={600} sx={{ flex: 1 }}>
          {authorName(item.author)}
        </Typography>
        {renderMenuButton(item)}
      </Box>
    );
  }

  return (
    <Box data-cy="comment-thread-list">
      <FormControlLabel
        // MUI's default -11px margin pulls the switch left of the content edge.
        sx={{ ml: -0.5 }}
        control={
          <Switch
            size="small"
            checked={showResolved}
            onChange={(e) => setShowResolved(e.target.checked)}
            inputProps={{ 'aria-label': 'Show resolved' }}
          />
        }
        label="Show resolved"
        slotProps={{ typography: { variant: 'body2' } }}
      />
      {visible.map((thread) => (
        <Box
          key={thread._id}
          ref={(el: HTMLElement | null) => {
            rowRefs.current[thread._id] = el;
          }}
          data-cy="comment-thread"
          sx={{
            py: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
            color: thread.resolvedAt ? 'text.secondary' : 'text.primary',
            ...(highlightedThreadId === thread._id && {
              bgcolor: 'action.hover',
            }),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ flex: 1 }}>{renderHeader(thread, true)}</Box>
            {canResolveComments && (
              <Tooltip
                title={
                  thread.resolvedAt
                    ? 'Reopen this thread'
                    : 'Mark this thread resolved'
                }
              >
                <span>
                  {thread.resolvedAt ? (
                    <Button
                      size="small"
                      variant="text"
                      startIcon={
                        <CheckCircleIcon fontSize="small" color="success" />
                      }
                      data-cy="comment-reopen"
                      disabled={!!busy}
                      onClick={() =>
                        void run(`reopen:${thread._id}`, thread._id, () =>
                          onReopen(thread._id)
                        )
                      }
                    >
                      Reopen
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<CheckCircleOutlineIcon fontSize="small" />}
                      data-cy="comment-resolve"
                      disabled={!!busy}
                      onClick={() =>
                        void run(`resolve:${thread._id}`, thread._id, () =>
                          onResolve(thread._id)
                        )
                      }
                    >
                      Resolve
                    </Button>
                  )}
                </span>
              </Tooltip>
            )}
          </Box>
          {thread.resolvedAt && (
            <Typography
              variant="caption"
              color="text.secondary"
              data-cy="comment-resolved-by"
              sx={{ display: 'block', mt: 0.25 }}
            >
              {resolvedByLabel(thread)}
            </Typography>
          )}
          {renderBody(thread)}
          {thread.replies.map((reply) => (
            <Box key={reply._id} data-cy="comment-reply" sx={{ ml: 4, mt: 1 }}>
              {renderHeader(reply, false)}
              {renderBody(reply)}
            </Box>
          ))}
          {canComment &&
            (replyingId === thread._id ? (
              <Box sx={{ ml: 4, mt: 1 }}>
                <TextField
                  fullWidth
                  multiline
                  size="small"
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                  inputProps={{ maxLength: 2000, 'aria-label': 'reply' }}
                />
                <Box sx={{ mt: 0.5, display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    data-cy="comment-reply-send"
                    disabled={!replyBody.trim() || !!busy}
                    onClick={async () => {
                      if (
                        await run(`reply:${thread._id}`, thread._id, () =>
                          onReply(thread._id, replyBody.trim())
                        )
                      ) {
                        setReplyBody('');
                        setReplyingId(null);
                      }
                    }}
                  >
                    Reply
                  </Button>
                  <Button size="small" onClick={() => setReplyingId(null)}>
                    Cancel
                  </Button>
                </Box>
              </Box>
            ) : (
              <Button
                size="small"
                // Cancels the small button's 5px padding so the label lines up
                // with the comment body's left edge.
                sx={{ ml: -0.625, mt: 0.5 }}
                data-cy="comment-reply-open"
                onClick={() => {
                  setReplyingId(thread._id);
                  setReplyBody('');
                }}
              >
                Reply
              </Button>
            ))}
        </Box>
      ))}
      <Menu
        id={menu ? `comment-menu-${menu.item._id}` : undefined}
        anchorEl={menu?.anchorEl}
        open={!!menu}
        onClose={() => setMenu(null)}
      >
        {menu?.canEdit && (
          <MenuItem
            data-cy="comment-edit"
            onClick={() => {
              const item = menu.item;
              setMenu(null);
              setEditingId(item._id);
              setEditBody(item.body);
            }}
          >
            Edit
          </MenuItem>
        )}
        {menu?.canDelete && (
          <MenuItem
            data-cy="comment-delete"
            sx={{ color: 'error.main' }}
            onClick={() => {
              const id = menu.item._id;
              setMenu(null);
              setDeleteId(id);
            }}
          >
            Delete
          </MenuItem>
        )}
      </Menu>
      <Dialog
        open={!!deleteId}
        onClose={() => {
          if (busy) return;
          setDeleteId(null);
          setDeleteError(null);
        }}
      >
        <DialogTitle>Delete comment?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This cannot be undone. Deleting a comment also removes its replies.
          </DialogContentText>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={!!busy}
            onClick={() => {
              setDeleteId(null);
              setDeleteError(null);
            }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            data-cy="comment-delete-confirm"
            disabled={!!busy}
            onClick={async () => {
              const id = deleteId;
              if (!id || busy) return;
              setBusy(`delete:${id}`);
              setDeleteError(null);
              try {
                await onDelete(id);
                setDeleteId(null);
              } catch (e) {
                setDeleteError(e instanceof Error ? e.message : String(e));
              } finally {
                setBusy(null);
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
